//Library Imports
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Tooltip, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import store, { RootState } from '../../../../../store';
import { FC, useEffect, useMemo, useState } from 'react';
//Local Imports
import { TAB_ANALYTICS } from 'constants/paths';
import ContactListLoader from '../../ContactListLoader';
import { ANALYTICS_INTENT_DATA, DEFAULT_CAMPAIGN_IMAGE } from 'constants/types';
import VerticalDivider from 'assets/svgs/VerticalDivider';
import EmailEngagementHeader from './EmailEngagementHeader';
import TableSort from 'modules/Common/Components/TableSort';
import { getAnalyticsFilterId, getQueryCampaignId, getQueryWorkflowId } from 'modules/EmailBrowser/utils';
import { addCommaToNumber, getQueryAccountId, getQueryContactId } from 'utils';
import ExportAnalyticsPopup from 'modules/Analytics/Shared/ExportAnalyticsPopup';
import { getConstantCampaignEmailId, setSortDirection } from 'modules/Analytics/utils';
import { SubtitleMediaQuery, TitleMediaQuery, campaignWorkflowMediaQuery } from './EmailEngagementMediaQuery';
import {
  analyticsDatesFilterChangeEmailEngagement,
  getAccountsEmailEngagement,
  getContactsEmailEngagement,
  resetAccountsEmailEngagement,
  resetContactsEmailEngagement,
  setAccountsEmailEngagementEmailsByFilter,
  setSearchFlagEmailEngagement,
} from './actions';
//Icon Imports
import workflowIcon from 'assets/img/icons/workflowIcon.svg';
import Speed from '../../../../../assets/img/icons/Speed.svg';
import { ReactComponent as Share } from '../../../../../assets/img/icons/share.svg';
import { getDisplayLabel } from '../../utils';

interface IContactsContent {
  page?: string;
  tab?: string;
}

const EmailTitle_CommonCSS = {
  display: 'flex',
  alignItems: 'center',
  color: '#424b50',
  fontWeight: 500,
  fontSize: '12px',
  fontFamily: 'Roboto',
  lineHeight: '16px',
  letterSpacing: '-0.292px',
};

const CommonTypographyCSS = {
  color: '#424B50',
  fontFamily: 'Roboto',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '-0.417px',
};

const EmailTitle_CSS = {
  color: '#424b50',
  fontWeight: 500,
  fontSize: '20px',
  fontFamily: 'Roboto',
  lineHeight: '24px',
  letterSpacing: '-0.417px',
};

const { Text } = Typography;
const DEFAULT_PAGE_LIMIT = 20;

const EmailEngagement: FC<IContactsContent> = (props: IContactsContent) => {
  // Props
  const { page, tab } = props;

  // Redux states
  const emails = useSelector((state: RootState) => state.emailsEngagement.emails);
  const emailPage = useSelector((state: RootState) => state.emailsEngagement.emailPage);
  const endDateCustom = useSelector((state: RootState) => state.emailsEngagement.endDate);
  const loading = useSelector((state: RootState) => state.emailsEngagement.emailsLoading);
  const totalEmails = useSelector((state: RootState) => state.emailsEngagement.totalEmails);
  const isSearch = useSelector((state: RootState) => state.emailsEngagement.isSearch);
  const startDateCustom = useSelector((state: RootState) => state.emailsEngagement.startDate);
  const emailsByFilter = useSelector((state: RootState) => state.emailsEngagement.emailsByFilter);
  const excludeAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeAssetsIds);
  const selectedEmailIds = useSelector((state: RootState) => state.accountsContentEngagement.emailIds ?? []);
  const excludeCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeCampaignsIds);
  const appliedWorkflowsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedWorkflowsIds);
  const selectedCampaignsIds = useSelector((state: RootState) => state.accountsContentEngagement.campaignIds ?? []);
  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters);

  // Local States
  const [sortBy, setSortBy] = useState<string>('');
  const [ellipsis, setEllipsis] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [firstTime, setFirstTime] = useState<boolean>(true);
  const [emailsByOpens, setEmailsByOpens] = useState<string>('');
  const [emailsByClicks, setEmailsByClicks] = useState<string>('');
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'Status', label: 'Status' });
  const [sortType, setSortType] = useState<{ accessor: string; sortDirection: string }>({ accessor: '', sortDirection: 'none' });

  // Local variables
  const history = useHistory();
  const contactId = getQueryContactId();
  const accountId = getQueryAccountId();
  const queryCampaignId = getQueryCampaignId();
  const queryWorkflowId = getQueryWorkflowId();
  const fsId = getAnalyticsFilterId();
  const isAccountsPage = page === TAB_ANALYTICS.ACCOUNTS;
  let params = {
    ...(status.value !== 'Status' && { status: status.value }),
    ...(searchTerm && { searchTerm: searchTerm }),
    ...(emailsByOpens && { emailsByOpens: emailsByOpens }),
    ...(emailsByClicks && { emailsByClicks: emailsByClicks }),
  };

  let columnsData = useMemo(
    () => [
      {
        id: 'main',
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            id: 'email_name',
            Header: 'Email Title',
            accessor: 'email_name',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'email_name' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="email-title">
                  <img
                    src={item.thumbnail !== null && item.thumbnail !== '' ? item.thumbnail : DEFAULT_CAMPAIGN_IMAGE}
                    alt=""
                    onError={(e: any) => {
                      e.target.onerror = null;
                    }}
                  />

                  <div className="details">
                    <div className="email_title">
                      <Text ellipsis={ellipsis ? { tooltip: item.title } : false}>{item.title}</Text>
                    </div>
                    <div className="email_subject">
                      <span className="sub-title">Subject:</span>
                      <Text ellipsis={{ tooltip: item.subject }}>{item.subject}</Text>
                    </div>
                    <div className="email_sent">
                      <span className="sub-title">Sent by: </span>
                      <Text ellipsis={{ tooltip: item.sentBy }}>{item.sentBy}</Text>
                    </div>
                    <div
                      className="view-email"
                      onClick={() => {
                        if (![1, 2, 3].includes(item.id)) {
                          window.open(
                            `/campaigns/editionedit/publish?campaign_id=${item.campaignId}&email_id=${item.id}${
                              item.generationId ? `&generation_id=${item.generationId}` : ''
                            }`,
                          );
                        }
                      }}
                    >
                      View Email <Share />
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            id: 'sent_date',
            Header: 'Sent',
            accessor: 'sent_date',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'sent_date' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return <div className="email_sent_date">{item.sentDate}</div>;
            },
          },
          {
            id: 'campaign_name',
            Header: 'Workflow/Campaign',
            accessor: 'campaign_name',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'campaign_name' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div
                  className="campaign-workflow d-flex align-items-center"
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: item.workflowId ? '3px' : '0px' }}
                >
                  {item.workflowId ? (
                    <>
                      <a
                        className={[1, 2, 3].includes(item.id) ? 'disabled' : ''}
                        href={`/automation/details?workflow_id=${item.workflowId}&redirect=automation`}
                        target="_blank"
                      >
                        <Tooltip title={item.workflowName}>
                          <img src={workflowIcon} alt="workflow-icon" style={{ marginRight: '8px' }} />
                        </Tooltip>
                      </a>
                      <span className="divider">
                        <VerticalDivider />
                      </span>
                    </>
                  ) : null}
                  <a
                    className={[1, 2, 3].includes(item.id) ? 'disabled' : ''}
                    href={`/campaigns/content?campaign_id=${item.campaignId}`}
                    target="_blank"
                  >
                    <div className="campaign-workflow-text">
                      <Text ellipsis={ellipsis ? { tooltip: item.campaignName } : false} style={{ paddingLeft: item.workflowId ? '' : '2px' }}>
                        {item.campaignName}
                      </Text>
                    </div>
                  </a>
                </div>
              );
            },
          },
          {
            id: isAccountsPage ? 'sent_count' : 'status',
            Header: isAccountsPage ? 'Sent (Delivered %)' : 'Status',
            accessor: isAccountsPage ? 'sent_count' : 'status',
            sortType: 'basic',
            sortDirection: isAccountsPage
              ? sortType.accessor === 'sent_count'
                ? sortType.sortDirection
                : 'none'
              : sortType.accessor === 'status'
              ? sortType.sortDirection
              : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className={`${!isAccountsPage ? (item.status === 'delivered' ? 'statusDelivered' : 'statusBounced') : ''}`}>
                  {isAccountsPage ? (
                    <Text
                    // ellipsis={{ tooltip: item.sentCount ? item.sentCount + Math.floor(item.deliveryRate) % + '%' : '' }}
                    >
                      {item.sentCount} ({Math.floor(item.deliveryRate)}%)
                    </Text>
                  ) : (
                    item.status
                      ?.split('_')
                      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')
                  )}
                </div>
              );
            },
          },
          {
            id: isAccountsPage ? 'unique_opens' : 'total_opens',
            Header: isAccountsPage ? 'Unique Opens' : 'Total Opens',
            accessor: isAccountsPage ? 'unique_opens' : 'total_opens',
            sortType: 'basic',
            sortDirection: isAccountsPage
              ? sortType.accessor === 'unique_opens'
                ? sortType.sortDirection
                : 'none'
              : sortType.accessor === 'total_opens'
              ? sortType.sortDirection
              : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((isAccountsPage ? item.uniqueOpens : item.totalOpens ?? 0)?.toString() ?? '');
            },
          },
          {
            id: isAccountsPage ? 'unique_clicks' : 'total_clicks',
            Header: isAccountsPage ? 'Unique Clicks' : 'Total Clicks',
            accessor: isAccountsPage ? 'unique_clicks' : 'total_clicks',
            sortType: 'basic',
            sortDirection: isAccountsPage
              ? sortType.accessor === 'unique_clicks'
                ? sortType.sortDirection
                : 'none'
              : sortType.accessor === 'total_clicks'
              ? sortType.sortDirection
              : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((isAccountsPage ? item.uniqueClicks : item.totalClicks ?? 0)?.toString() ?? '');
            },
          },
          {
            id: 'score',
            Header: 'Score',
            accessor: 'score',
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item hand-cursor">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    <span>{addCommaToNumber((item.score ?? 0)?.toString() ?? '')}</span>
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType],
  );

  let timeoutId: any;

  const debounce = (callback: any, wait: any, column: any) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      callback(column);
    }, wait);
  };

  const sortedResults = (column?: any) => {
    let sort_type = '';
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      let _params = {
        ...params,
        sortType: sort_type,
        sortBy: column.id,
        resetOffset: true,
      };
      if (isAccountsPage) {
        resetAccountsEmailEngagement();
        getAccountsEmailEngagement(getPagination(_params));
      } else {
        resetContactsEmailEngagement();
        getContactsEmailEngagement(getPagination(_params));
      }
    }
  };

  const getPagination = (params?: any) => {
    let paramStatus: string = '';
    if (params?.status) {
      if (status.value === 'Delivered') paramStatus = 'delivered';
      else if (status.value === 'Soft Bounce') paramStatus = 'soft_bounce';
      else if (status.value === 'Hard Bounce') paramStatus = 'hard_bounce';
    }
    // to update emails ids based on filters we need to clone the arrays
    let selected_emails_ids = [...selectedEmailIds];
    let selected_campaings_ids = [...selectedCampaignsIds];
    let selected_workflows_ids = [...appliedWorkflowsIds];

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      selected_campaings_ids = [campaign_email_id.toString()];
      selected_emails_ids = [campaign_email_id.toString()];
    }
    return {
      ...(isAccountsPage ? { account_id: getQueryAccountId() } : { contact_id: getQueryContactId() }),
      offset: params?.resetOffset ? 0 : emailPage > 0 ? emails.length : 0,
      limit: DEFAULT_PAGE_LIMIT,
      start_date: startDateCustom,
      end_date: endDateCustom,
      ...(isAccountsPage && { campaignIds: queryCampaignId ? [queryCampaignId] : selected_campaings_ids }), // selected campaigns ids
      ...(isAccountsPage && { emailIds: selected_emails_ids, workflowIds: selected_workflows_ids, allCampaigns: AllCampaignsFilterCheck }),
      ...(isAccountsPage && AllCampaignsFilterCheck && { excludeCampaignsIds: excludeCampaignsIds, excludeAssetsIds: excludeAssetsIds }),
      ...(params?.sortBy && params?.sortType !== 'none' && { sort_by: params.sortBy }),
      ...(params?.sortType && params?.sortType !== 'none' && { sort_type: params.sortType }),
      ...(!isAccountsPage && params?.status && { status: paramStatus }),
      ...(params?.searchTerm && { search_term: searchTerm }),
      ...(params?.emailsByOpens && isAccountsPage ? { email_by_unique_opens: emailsByOpens } : { email_by_total_opens: emailsByOpens }),
      ...(params?.emailsByClicks && isAccountsPage ? { email_by_unique_clicks: emailsByClicks } : { email_by_total_clicks: emailsByClicks }),
    };
  };

  const debouncedSorting = (column?: any) => {
    debounce(sortedResults, 1000, column);
  };

  useEffect(() => {
    if (!emails.length || (accountId >= 0 && contactId)) {
      if (!isAccountsPage && accountId >= 0 && contactId) {
        setEmailsByOpens('');
        setEmailsByClicks('');
        setAccountsEmailEngagementEmailsByFilter('');
        analyticsDatesFilterChangeEmailEngagement({ label: '1 QTR', value: 'qtr' }, isAccountsPage);
      }
      if (isAccountsPage) {
        resetAccountsEmailEngagement();
        getAccountsEmailEngagement(getPagination({ resetOffset: true }));
      } else if (isSearch || !emails.length) {
        resetContactsEmailEngagement();
        getContactsEmailEngagement(getPagination({ resetOffset: true }));
        setSearchFlagEmailEngagement(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!firstTime) {
      let _params = {
        ...params,
        resetOffset: true,
        ...(sortBy && { sortBy: sortBy }),
        ...(sortType.accessor && { sortType: sortType.sortDirection }),
      };
      if (isAccountsPage) {
        resetAccountsEmailEngagement();
        getAccountsEmailEngagement(getPagination(_params));
      } else {
        resetContactsEmailEngagement();
        getContactsEmailEngagement(getPagination(_params));
      }
    } else {
      if (emailsByFilter) {
        const selectedLabel = getDisplayLabel(emailsByFilter.split(' ')[0]);
        let isOpens = isAccountsPage ? selectedLabel === 'Unique Opens' : selectedLabel === 'Total Opens';
        let isClicks = isAccountsPage ? selectedLabel === 'Unique Clicks' : selectedLabel === 'Total Clicks';
        const sign = emailsByFilter.split(' ')[1];
        const value = emailsByFilter.split(' ')[2];
        switch (sign) {
          case '=':
            isOpens && setEmailsByOpens && setEmailsByOpens('=' + value);
            isClicks && setEmailsByClicks && setEmailsByClicks('=' + value);
            break;
          case '<':
            isOpens && setEmailsByOpens && setEmailsByOpens('<' + value);
            isClicks && setEmailsByClicks && setEmailsByClicks('<' + value);
            break;

          case '>':
            isOpens && setEmailsByOpens && setEmailsByOpens('>' + value);
            isClicks && setEmailsByClicks && setEmailsByClicks('>' + value);
            break;
          case '<=':
            isOpens && setEmailsByOpens && setEmailsByOpens('<=' + value);
            isClicks && setEmailsByClicks && setEmailsByClicks('<=' + value);
            break;
          case '>=':
            isOpens && setEmailsByOpens && setEmailsByOpens('>=' + value);
            isClicks && setEmailsByClicks && setEmailsByClicks('>=' + value);
            break;
        }
      }
      setFirstTime(false);
    }
    if (searchTerm) setSearchFlagEmailEngagement(true);
    return () => {
      if (searchTerm) {
        if (isAccountsPage) {
          resetAccountsEmailEngagement();
        } else {
          resetContactsEmailEngagement();
        }
      }
      setSearchFlagEmailEngagement(false);
    };
  }, [searchTerm, startDateCustom, endDateCustom, emailsByOpens, emailsByClicks, status.value, emailsByFilter]);

  return (
    <EmailEngagementWrapper>
      <EmailEngagementHeader
        tab={tab}
        page={page}
        status={status}
        searchTerm={searchTerm}
        setStatus={setStatus}
        setSearchTerm={setSearchTerm}
        setEmailsByOpens={setEmailsByOpens}
        setEmailsByClicks={setEmailsByClicks}
        emailsByOpens={emailsByOpens}
        emailsByClicks={emailsByClicks}
      />
      <Wrapper accountPage={isAccountsPage}>
        <div style={{ width: '100%' }}>
          <TableSort
            wrapperClassName="email-engagement-user"
            columns={columnsData}
            data={emails}
            module={'email_engagement'}
            _onCellClick={debouncedSorting}
            _onRowClick={(e, org) => {}}
            next={() => {
              let _params = {
                ...params,
                ...(sortBy && { sortBy: sortBy }),
                ...(sortType.accessor && { sortType: sortType.sortDirection }),
              };
              if (isAccountsPage) {
                getAccountsEmailEngagement(getPagination(_params));
              } else {
                getContactsEmailEngagement(getPagination(_params));
              }
            }}
            hasMore={emails.length < totalEmails}
            loading={loading}
            loader={<ContactListLoader loading={loading} />}
          />
          {!loading && emails !== undefined && Object.keys(emails).length === 0 ? <div className="no-data">No Data Found!</div> : null}
        </div>
      </Wrapper>
      {isAccountsPage ? (
        <ExportAnalyticsPopup
          tab={tab}
          searchedText={''}
          searchTerm={searchTerm}
          status={status}
          isFromAccounts={isAccountsPage}
          emailsByOpens={emailsByOpens}
          emailsByClicks={emailsByClicks}
        />
      ) : null}
    </EmailEngagementWrapper>
  );
};

export default EmailEngagement;
const EmailEngagementWrapper = styled.div`
  .email-count-wrapper {
    margin-top: 19px;
  }
`;
const Wrapper = styled.div<{
  accountPage?: boolean;
}>`
  overflow: hidden;
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  #scrollableDiv .infinite-scroll-component {
    max-height: calc(100vh - 300px);
    min-height: auto;
  }
  .email-engagement-user {
    height: auto;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: auto;
    }
  }
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    min-height: 83px;
  }
  .last-item {
    .engagement {
      border-radius: 12px;
      background-color: #1971eb;
      color: #ffffff;
      font-size: 16px;
      font-family: 'Roboto';
      font-weight: 500;
      padding-top: 3px;
      padding-bottom: 2px;
      padding-left: 8px;
      padding-right: 8px;
      display: flex;
      align-items: center;
      white-space: nowrap;
      line-height: 18px;
      letter-spacing: -0.333px;
      span {
        padding-left: 3.7px;
      }
      @media all and (max-width: 1280px) {
        font-size: 13px;
      }
    }
  }
  .hand-cursor {
    cursor: pointer;
  }
  .no-data {
    font-family: 'robotomedium';
    color: #67696a;
    font-size: 1rem;
    border-radius: 5px;
    position: absolute;
    bottom: 25%;
    left: 50%;
  }
  table {
    width: 100%;
    margin-top: 20px;
    display: block;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        th {
          color: #5e7079;
          font-weight: 500;
          font-size: 16px;
          font-family: 'Roboto';
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      > div {
        overflow: visible;
        height: ${(props) => (props.accountPage ? 'calc(100vh - 394px)' : 'calc(100vh - 350px)')};
        @media all and (min-width: 1440px) and (-webkit-device-pixel-ratio: 1) {
          height: ${(props) => (props.accountPage ? 'calc(100vh - 425px)' : 'calc(100vh - 380px)')};
        }
        @media all and (max-width: 1024px) {
          height: ${(props) => (props.accountPage ? 'calc(100vh - 394px)' : 'calc(100vh - 370px)')};
        }
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 8px 0;
        padding: 12px 16px;
        /* cursor: pointer; */
      }
    }
    thead {
      tr {
        th {
          span {
            width: 15px;
            .sort_asc {
              width: 10px;
              height: 8px;
              margin-top: -15px;
              margin-left: 5px;
              float: left;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: -15px;
              }
              @media all and (max-width: 1200px) {
                margin-top: -11px;
              }
              img {
                transform: rotate(180deg);
              }
            }
            .sort_desc {
              width: 10px;
              height: 8px;
              float: left;
              margin-left: 5px;
              margin-top: 5px;
              img {
                float: left;
              }
            }
          }
        }
      }

      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: 'Roboto';
          white-space: nowrap;
          display: flex;
          align-items: center;
          @media all and (max-width: 1200px) {
            font-size: 12px;
          }
        }
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      display: grid;
      tr {
        width: 100%;
        display: grid;
        grid-template-columns: ${(props) => (props.accountPage ? '3fr 1fr 1.3fr 1.2fr 1fr 1fr 0.7fr' : '3fr 1fr 1.5fr 0.8fr 1fr 1fr 0.7fr')};
        @media all and (max-width: 1440px) {
          grid-template-columns: ${(props) => (props.accountPage ? '3.3fr 1fr 1.6fr 1.5fr 1.2fr 1.2fr 0.7fr' : '3fr 1fr 1.8fr 0.8fr 1fr 1fr 0.7fr')};
        }
        @media all and (max-width: 1280px) {
          grid-template-columns: ${(props) =>
            props.accountPage ? '2.5fr 1fr 1.8fr 1.5fr 1.2fr 1.2fr 0.7fr' : '2.5fr 1fr 1.7fr 0.8fr 1fr 1fr 0.7fr'};
        }
        td {
          min-height: 70px;
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          font-size: 20px;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 13px;
          }
        }
        th,
        td {
          display: flex;
          align-items: center;
          &:nth-child(1) {
            > div {
              display: flex;
              flex-direction: row;
              align-items: center;
              width: 100%;
              img {
                border-radius: 5px;
                border: 0.6px solid #dadada;
                max-width: 84px;
                min-width: 84px;
                height: 69px;
                @media all and (max-width: 1439px) and (-webkit-device-pixel-ratio: 1.25) {
                  max-width: 50px;
                  min-width: 50px;
                  height: 50px;
                }
                @media all and (max-width: 1152px) {
                  max-width: 45px;
                  min-width: 45px;
                  height: 45px;
                }
              }
              .details {
                align-self: center;
                margin-left: 12px;
                width: 100%;
                .email_title {
                  ${EmailTitle_CSS}
                  display: flex;
                  .ant-typography-ellipsis-single-line {
                    max-width: 241px;
                  }
                  ${TitleMediaQuery}
                }
                .email_subject {
                  ${EmailTitle_CommonCSS};
                  .ant-typography-ellipsis-single-line {
                    max-width: 180px;
                  }
                  ${SubtitleMediaQuery}
                }
                .email_sent {
                  ${EmailTitle_CommonCSS}
                  .ant-typography-ellipsis-single-line {
                    max-width: 180px;
                  }
                  ${SubtitleMediaQuery}
                }
                .view-email {
                  cursor: pointer;
                  ${EmailTitle_CommonCSS}
                  color: #1EC252;
                  svg {
                    margin-left: 4px;
                  }
                }
                .sub-title {
                  ${EmailTitle_CommonCSS}
                  margin-right: 4px;
                  font-weight: 400;
                  @media all and (max-width: 1200px) {
                    font-size: 11px;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            text-align: left;
            display: inline-flex;
            align-items: center;
            &[role='cell'] {
              padding-left: ${(props) => (props.accountPage ? '2px' : '3px')};
            }
            > div {
              display: block;
              ${CommonTypographyCSS}
              @media all and (max-width: 1280px) {
                font-size: 13px;
              }
            }
          }
          &:nth-child(3) {
            display: inline-flex;
            text-align: left;
            align-items: center;
            .campaign-workflow {
              .campaign-workflow-text {
                font-family: 'Roboto';
                font-size: 16px;
                font-style: normal;
                font-weight: 500;
                line-height: 20px;
                letter-spacing: -0.4px;
                .ant-typography-ellipsis-single-line {
                  max-width: 160px;
                  padding-left: 8px;
                  color: #1ec252;
                }
                ${campaignWorkflowMediaQuery}
              }
            }
          }
          &:nth-child(4) {
            text-align: left;
            display: flex;
            align-items: center;
            .statusDelivered {
              color: #001760;
            }
            .statusBounced {
              color: #ff9531;
            }
            &[role='cell'] {
              justify-content: ${(props) => (props.accountPage ? 'center' : 'start')};
              padding-left: ${(props) => (props.accountPage ? '0px' : '4px')};
            }
            > div {
              display: block;
              padding-right: ${(props) => (props.accountPage ? '16px' : '0px')};
              ${CommonTypographyCSS}
              @media all and (max-width: 1280px) {
                font-size: 13px;
                padding-right: ${(props) => (props.accountPage ? '0px' : '0px')};
              }
              @media all and (min-width: 1920px) and (-webkit-device-pixel-ratio: 1) {
                padding-right: ${(props) => (props.accountPage ? '50px' : '0px')};
              }
              /* .ant-typography-ellipsis-single-line {
                  max-width: 70px;
                  width: 70px;
                } */
            }
          }
          &:nth-child(5) {
            display: flex;
            align-items: center;
            &[role='cell'] {
              color: #424b50;
              font-family: 'Roboto';
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
              padding-left: 50px;
              @media all and (max-width: 1280px) and (-webkit-device-pixel-ratio: 1.25) {
                font-size: 13px;
                padding-left: 40px;
              }
              @media all and (max-width: 1152px) and (-webkit-device-pixel-ratio: 1) {
                font-size: 13px;
                padding-left: 40px;
              }
            }
          }
          &:nth-child(6) {
            display: flex;
            align-items: center;
            &[role='cell'] {
              color: #424b50;
              font-family: 'Roboto';
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
              padding-left: 50px;
              @media all and (max-width: 1280px) and (-webkit-device-pixel-ratio: 1.25) {
                font-size: 13px;
                padding-left: 40px;
              }
              @media all and (max-width: 1152px) and (-webkit-device-pixel-ratio: 1) {
                font-size: 13px;
                padding-left: 40px;
              }
            }
          }
          &:nth-child(7) {
            display: inline-flex;
            align-items: center;
            .last-item {
              margin-left: 6px;
            }
            &[role='cell'] {
              color: #424b50;
              font-family: 'Roboto';
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
              @media all and (max-width: 1280px) {
                font-size: 13px;
              }
            }
          }
        }
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: 'Roboto', sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
